import { useEffect, useState, useCallback } from "react";
import { Loader2, Wallet, Tag, XCircle } from "lucide-react";
import { Contract, JsonRpcProvider, parseEther, formatEther } from "ethers";
import { useWallet } from "../contexts/WalletContext";
import { NFT_ADDRESS, MARKETPLACE_ADDRESS, PhygitalNFTAbi, NFTMarketplaceAbi, DEPLOYED_CHAIN_ID } from "../contracts";
import { fetchMetadata } from "../utils/ipfs";
import { POLYGON_RPC_URL } from "../config/web3Config";
import NFTImage from "../components/NFTImage";
import Toast from "../components/Toast";

interface NFTItem {
  tokenId:      bigint;
  name:         string;
  description:  string;
  image:        string;
  isGenesis:    boolean;
  hasListing:   boolean;
  listingPrice: bigint;
}
interface ToastState { message: string; type: "success"|"error"|"info"|"pending"; txHash?: string; }

export default function MyNFTsPage() {
  const { isConnected, signer, account, chainId } = useWallet();
  const [nfts,      setNfts]      = useState<NFTItem[]>([]);
  const [loading,   setLoading]   = useState(true);
  const [working,   setWorking]   = useState<bigint | null>(null);
  const [prices,    setPrices]    = useState<Record<string, string>>({});
  const [toast,     setToast]     = useState<ToastState | null>(null);

  const loadMyNFTs = useCallback(async () => {
    if (!account) return;
    try {
      setLoading(true);
      const provider = new JsonRpcProvider(POLYGON_RPC_URL);
      const nft = new Contract(NFT_ADDRESS, PhygitalNFTAbi, provider);
      const mkt = new Contract(MARKETPLACE_ADDRESS, NFTMarketplaceAbi, provider);
      const ids: bigint[] = await nft.tokensOfOwner(account);
      const items = await Promise.all(ids.map(async id => {
        const [uri, listing] = await Promise.all([
          nft.tokenURI(id) as Promise<string>,
          mkt.getListing(NFT_ADDRESS, id) as Promise<{ seller: string; price: bigint; active: boolean }>,
        ]);
        const meta = await fetchMetadata(uri);
        return { tokenId: id, name: meta.name, description: meta.description, image: meta.image,
          isGenesis: id === 1n, hasListing: listing.active, listingPrice: listing.price };
      }));
      setNfts(items);
    } catch (e) { console.error("loadMyNFTs:", e); }
    finally { setLoading(false); }
  }, [account]);

  useEffect(() => { if (isConnected && account) loadMyNFTs(); else setLoading(false); }, [isConnected, account, loadMyNFTs]);

  const listForSale = async (tokenId: bigint) => {
    const priceStr = prices[tokenId.toString()];
    if (!priceStr || parseFloat(priceStr) <= 0) { setToast({ message: "Enter a valid price in POL", type: "error" }); return; }
    if (!signer) return;
    try {
      setWorking(tokenId);
      const nftW = new Contract(NFT_ADDRESS, PhygitalNFTAbi, signer);
      const mktW = new Contract(MARKETPLACE_ADDRESS, NFTMarketplaceAbi, signer);
      // Approve if not yet approved
      const approved: string = await nftW.getApproved(tokenId);
      if (approved.toLowerCase() !== MARKETPLACE_ADDRESS.toLowerCase()) {
        setToast({ message: "Step 1/2 — Approving marketplace…", type: "pending" });
        const appTx = await nftW.approve(MARKETPLACE_ADDRESS, tokenId);
        setToast({ message: "Approval submitted…", type: "pending", txHash: appTx.hash });
        await appTx.wait();
      }
      setToast({ message: "Step 2/2 — Listing NFT…", type: "pending" });
      const tx = await mktW.listNFT(NFT_ADDRESS, tokenId, parseEther(priceStr));
      setToast({ message: "Listing submitted…", type: "pending", txHash: tx.hash });
      await tx.wait();
      setToast({ message: `Token #${tokenId} listed for ${priceStr} POL!`, type: "success", txHash: tx.hash });
      setPrices(p => { const n = {...p}; delete n[tokenId.toString()]; return n; });
      loadMyNFTs();
    } catch (e: any) {
      setToast({ message: e?.reason ?? e?.message ?? "Listing failed", type: "error" });
    } finally { setWorking(null); }
  };

  const delistNFT = async (tokenId: bigint) => {
    if (!signer) return;
    try {
      setWorking(tokenId);
      setToast({ message: "Cancelling listing…", type: "pending" });
      const mktW = new Contract(MARKETPLACE_ADDRESS, NFTMarketplaceAbi, signer);
      const tx = await mktW.delistNFT(NFT_ADDRESS, tokenId);
      setToast({ message: "Submitted…", type: "pending", txHash: tx.hash });
      await tx.wait();
      setToast({ message: "Listing cancelled!", type: "success", txHash: tx.hash });
      loadMyNFTs();
    } catch (e: any) {
      setToast({ message: e?.reason ?? e?.message ?? "Cancel failed", type: "error" });
    } finally { setWorking(null); }
  };

  if (!isConnected) return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="text-center">
        <Wallet className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h2 className="text-2xl font-semibold text-gray-700 mb-2">Connect Your Wallet</h2>
        <p className="text-gray-500">Connect to manage your NFTs</p>
      </div>
    </div>
  );
  if (loading) return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="text-center">
        <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
        <p className="text-gray-600">Loading your NFTs…</p>
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {toast && <Toast {...toast} chainId={chainId || DEPLOYED_CHAIN_ID} onClose={() => setToast(null)} />}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-1">My NFTs</h1>
        <p className="text-gray-500">Manage your owned NFTs — update listings or cancel them</p>
      </div>

      {nfts.length === 0 ? (
        <div className="flex items-center justify-center min-h-[300px]">
          <div className="text-center">
            <Wallet className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-700 mb-2">No NFTs in this wallet</h2>
            <p className="text-gray-500">Browse the marketplace to purchase NFTs!</p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {nfts.map(nft => (
            <div key={nft.tokenId.toString()}
              className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-shadow duration-300">
              {nft.isGenesis && (
                <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-bold text-center py-1 tracking-widest uppercase">
                  ◆ Genesis NFT
                </div>
              )}
              <NFTImage tokenId={Number(nft.tokenId)} image={nft.image} name={nft.name} />
              <div className="p-4">
                <h3 className="font-bold text-gray-900 truncate mb-0.5">{nft.name}</h3>
                {nft.hasListing && (
                  <p className="text-sm text-blue-600 font-semibold mb-2">
                    Listed: {parseFloat(formatEther(nft.listingPrice)).toFixed(4)} POL
                  </p>
                )}
                <div className="space-y-2 mt-3">
                  <div className="flex gap-2">
                    <input
                      type="number" step="0.001" min="0.001"
                      placeholder={nft.hasListing ? "New price (POL)" : "Price (POL)"}
                      value={prices[nft.tokenId.toString()] ?? ""}
                      onChange={e => setPrices(p => ({ ...p, [nft.tokenId.toString()]: e.target.value }))}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                  </div>
                  <button onClick={() => listForSale(nft.tokenId)} disabled={working === nft.tokenId}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                    {working === nft.tokenId
                      ? <><Loader2 className="w-4 h-4 animate-spin" />Working…</>
                      : <><Tag className="w-4 h-4" />{nft.hasListing ? "Relist" : "List for Sale"}</>}
                  </button>
                  {nft.hasListing && (
                    <button onClick={() => delistNFT(nft.tokenId)} disabled={working === nft.tokenId}
                      className="w-full bg-red-50 hover:bg-red-100 text-red-600 px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                      <XCircle className="w-4 h-4" />Cancel Listing
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
